/**
 * @fileoverview Input/output schemas for the quick-image-v2 composite agent.
 *
 * This composite orchestrates:
 * 1. story-image-prompt-creator-v2: Generates a prompt from prose
 * 2. story-image-creator-fal-v2: Generates an image from the prompt
 *
 * For zero-interaction "quick" image generation from selected text.
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

/**
 * Minimal input from UI/hooks.
 */
export const HookInputSchema = z.object({
  selectedText: z.string().min(1).describe('The prose text selected by the user.'),
  chapterContent: z.string().describe('The full chapter content for context.'),
  chapterId: z.string().optional().describe('The chapter ID for metadata.'),
  aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).optional().describe('Image aspect ratio. Defaults to square.'),
  enableSafetyChecker: z.boolean().optional().describe('Enable FAL safety checker. Defaults to false.'),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the composite flow.
 * Extends base flow input with story context and image generation options.
 */
export const InputSchema = CompositeBaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.charactersCurrent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.worldElements.schema)
  .merge(AGENT_SCHEMA_OBJECTS.selectedText.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContent.schema)
  .extend({
    chapterId: z.string().optional().describe('The chapter ID for metadata tracking.'),
    aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).default('square').describe('Image aspect ratio.'),
    enableSafetyChecker: z.boolean().default(false).describe('Enable FAL safety checker.'),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  /** The generated image as a Base64-encoded data URI */
  imageDataUri: z.string().describe('The generated image as a Base64-encoded data URI.'),
  /** The prompt that was generated and used for image creation */
  prompt: z.string().describe('The generated prompt used for image creation.'),
  /** Characters identified in the selected prose */
  characterNames: z.array(z.string()).optional().describe('Characters identified in the prose.'),
  /** World elements identified in the selected prose */
  worldElementNames: z.array(z.string()).optional().describe('World elements identified in the prose.'),
  /** The seed used for image generation, for reproducibility */
  seed: z.number().optional().describe('The seed used for image generation, if returned by the model.'),
  /** Step-level execution details */
  steps: z.object({
    promptCreation: z.object({
      durationMs: z.number().describe('Time taken to generate the prompt.'),
      model: z.string().describe('Model used for prompt generation.'),
    }),
    imageGeneration: z.object({
      durationMs: z.number().describe('Time taken to generate the image.'),
      model: z.string().describe('Model used for image generation.'),
    }),
  }).describe('Execution details for each step.'),
});

export type Output = z.infer<typeof OutputSchema>;
